/* eslint-disable */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ArrowLeft from 'components/shared/icons/arrow-left'
import ArrowRight from 'components/shared/icons/arrow-right'
import Select from 'react-select'
import { PERPAGE_OPTIONS, DOTS } from 'constants/pagination'
import { range } from 'utils/paginationHelpers'

const buttonClasses =
  'flex justify-center items-center h-10 w-10 border border-l-0 border-gray-300 text-center leading-8 '

const TablePagination = ({
  onPageChange,
  onPageSizeChange,
  totalCount = 0,
  siblingCount = 1,
  currentPage = 1,
  pageSize,
  className = '',
}) => {
  const totalPageCount = Math.ceil(totalCount / pageSize)
  const fromIndex = ((currentPage - 1) * pageSize) + 1
  const toIndex = currentPage * pageSize

  const paginationRange = useMemo(() => {
    const totalPageNumbers = siblingCount + 5

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount)
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount)

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2

    const firstPageIndex = 1
    const lastPageIndex = totalPageCount

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + (2 * siblingCount)
      const leftRange = range(1, leftItemCount)

      return [...leftRange, DOTS, totalPageCount]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + (2 * siblingCount)
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount)
      return [firstPageIndex, DOTS, ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex]
    }
  }, [totalCount, pageSize, siblingCount, currentPage])

  const handleNextPage = () => onPageChange(currentPage + 1)
  const handlePreviousPage = () => onPageChange(currentPage - 1)

  return (
    <div className={`flex px-8 justify-between items-center bg-white ${className}`}>
      <div>
        <p className="mb-1">
          <b>{fromIndex}</b> to <b>{toIndex < totalCount ? toIndex : totalCount}</b> of <b>{totalCount}</b> records
        </p>
        <div className="flex items-center">
          <div className="w-24">
            <Select
              classNamePrefix="react-select"
              options={PERPAGE_OPTIONS}
              defaultValue={PERPAGE_OPTIONS[0]}
              onChange={({ value }) => {
                onPageSizeChange(value)
                onPageChange(1)
              }}
            />
          </div>
          <span className="ml-2">records</span>
        </div>
      </div>

      {
        <div className="flex text-sm font-medium">
          <button
            className={`${buttonClasses} border-l ${
              currentPage === 1 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
            }`}
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            <ArrowLeft width="18px" height="18px" />
          </button>
          {paginationRange?.map((pageNumber, index) => {
            return pageNumber === DOTS ? (
              <button key={index} className={buttonClasses}>
                ...
              </button>
            ) : (
              <button
                key={index}
                className={buttonClasses + (pageNumber === currentPage ? 'bg-primary-orange' : '')}
                onClick={() => onPageChange(pageNumber)}
              >
                {pageNumber}
              </button>
            )
          })}
          <button
            className={
              buttonClasses +
              (currentPage === totalPageCount ? 'bg-gray-100 cursor-not-allowed' : '')
            }
            onClick={handleNextPage}
            disabled={currentPage === totalPageCount}
          >
            <ArrowRight width="18px" height="18px" />
          </button>
        </div>
      }
    </div>
  )
}

TablePagination.propTypes = {
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  totalCount: PropTypes.number,
  perPage: PropTypes.number,
  siblingCount: PropTypes.number,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  className: PropTypes.string,
}

export default TablePagination
